import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Table, Alert, message, Select, Button, Space, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { formatVnDateTimeShort } from '../../utils/vnDateTime';
import {
  fetchMerchantRegistrationLeads,
  type LeadStatus,
  type MerchantRegistrationLeadRow,
} from '../../services/merchantRegistrationLeadsService';
import { EmptyState } from '../../design-system/EmptyState';
import { MerchantRegistrationKycModal } from './MerchantRegistrationKycModal';

const ROUNDED_CARD_STYLE: React.CSSProperties = {
  borderRadius: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

const PAGE_SIZE = 20;

function statusTag(status: string | undefined, kycSavedAt?: string | null) {
  const s = (status || 'PENDING') as LeadStatus;
  if (s === 'APPROVED') {
    return <Tag color="success">Đã duyệt</Tag>;
  }
  if (kycSavedAt) {
    return <Tag color="processing">Đã lưu KYC</Tag>;
  }
  return <Tag color="warning">Chờ KYC</Tag>;
}

export default function MerchantRegistrationLeadsPage() {
  const [rows, setRows] = useState<MerchantRegistrationLeadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<LeadStatus | 'ALL'>('ALL');
  const [approveLead, setApproveLead] =
    useState<MerchantRegistrationLeadRow | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchMerchantRegistrationLeads({
        page,
        limit: pageSize,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
      });
      if (res.success && res.data) {
        setRows(res.data);
        setTotal(res.pagination?.total ?? 0);
      } else {
        setRows([]);
        setTotal(0);
        setError(res.message || 'Không tải được danh sách');
      }
    } catch (e: unknown) {
      const ax = e as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      setError(ax.response?.data?.message || ax.message || 'Lỗi tải dữ liệu');
      message.error('Không tải được danh sách đăng ký');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: ColumnsType<MerchantRegistrationLeadRow> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 72,
      fixed: 'left',
    },
    {
      title: 'Tên quán',
      dataIndex: 'store_name',
      key: 'store_name',
      ellipsis: true,
    },
    {
      title: 'Chủ quán',
      dataIndex: 'owner_name',
      key: 'owner_name',
      width: 140,
      ellipsis: true,
    },
    {
      title: 'SĐT',
      dataIndex: 'phone',
      key: 'phone',
      width: 130,
    },
    {
      title: 'Loại hình',
      dataIndex: 'business_type',
      key: 'business_type',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Địa chỉ',
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
    },
    {
      title: 'Nguồn',
      dataIndex: 'source',
      key: 'source',
      width: 100,
      render: (v: string | null) => v || '—',
    },
    {
      title: 'IP',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 120,
      render: (v: string | null) => v || '—',
    },
    {
      title: 'Gửi lúc',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (v: string) => (v ? formatVnDateTimeShort(v) : '—'),
    },
    {
      title: 'Duyệt lúc',
      dataIndex: 'approved_at',
      key: 'approved_at',
      width: 160,
      render: (v: string | null | undefined) =>
        v ? formatVnDateTimeShort(v) : '—',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      fixed: 'right',
      render: (v: string | undefined, record) =>
        statusTag(v, record.kyc_saved_at),
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_: unknown, record: MerchantRegistrationLeadRow) => {
        const s = (record.status || 'PENDING') as LeadStatus;
        if (s === 'APPROVED') {
          return <span style={{ color: 'rgba(0,0,0,0.45)' }}>—</span>;
        }
        return (
          <Button
            type="primary"
            size="small"
            onClick={() => {
              setApproveLead(record);
              setModalOpen(true);
            }}
          >
            KYC merchant
          </Button>
        );
      },
    },
  ];

  return (
    <div
      style={{
        padding: 24,
        maxWidth: 1600,
        margin: '0 auto',
      }}
    >
      <header className="merchant-detail-header">
        <h1 className="merchant-detail-title">Đăng ký quán (website)</h1>
        <nav className="merchant-detail-breadcrumb">
          <Link to="/">Dashboard</Link>
          <span className="merchant-detail-breadcrumb-sep">&gt;</span>
          <span className="merchant-detail-breadcrumb-current">
            Đăng ký từ Urban Hub
          </span>
        </nav>
      </header>

      <Card style={ROUNDED_CARD_STYLE} bodyStyle={{ padding: 24 }}>
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Space wrap>
            <span style={{ color: 'rgba(0,0,0,0.65)' }}>Lọc:</span>
            <Select<LeadStatus | 'ALL'>
              value={statusFilter}
              onChange={v => {
                setStatusFilter(v);
                setPage(1);
              }}
              style={{ width: 200 }}
              options={[
                { value: 'ALL', label: 'Tất cả' },
                { value: 'PENDING', label: 'Chờ duyệt' },
                { value: 'APPROVED', label: 'Đã duyệt' },
              ]}
            />
          </Space>
        </div>

        {error && (
          <Alert
            type="error"
            message={error}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {!error && rows.length === 0 && !loading && (
          <EmptyState
            description="Chưa có yêu cầu đăng ký nào"
            onRefresh={load}
          />
        )}

        {!error && (rows.length > 0 || loading) && (
          <Table<MerchantRegistrationLeadRow>
            rowKey="id"
            columns={columns}
            dataSource={rows}
            loading={loading}
            scroll={{ x: 1480 }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              showTotal: t => `${t} yêu cầu`,
              pageSizeOptions: ['10', '20', '50'],
              onChange: (p, ps) => {
                setPage(p);
                if (ps) setPageSize(ps);
              },
            }}
          />
        )}
      </Card>

      <MerchantRegistrationKycModal
        open={modalOpen}
        lead={approveLead}
        onClose={() => {
          setModalOpen(false);
          setApproveLead(null);
        }}
        onSuccess={load}
      />
    </div>
  );
}
